import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import type { AuthUser, AuthStatusResponse } from '../types/apiTypes';
import { configureReceivingFromServer } from '../services/receivingAPI';
import { clearLocalStorageKeepingRefreshDay, startDailyHardRefresh } from '../utils/dailyHardRefresh';

interface AuthContextValue {
  user: AuthUser | null;
  authenticated: boolean;
  loading: boolean;
  sessionExpired: boolean;
  isDev: boolean;
  isAdmin: boolean;
  /** When true, admins see the admin-only UI. Off by default. Does not change route guards. */
  showAdminUi: boolean;
  role: 'user' | 'admin' | 'dev' | 'owner' | null;
  login: () => void;
  logout: () => Promise<void>;
  toggleShowAdminUi: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/* New key: the previous `wms_view_as_user` flag carried the opposite meaning, so a
   stored value must not be reinterpreted here. Admins start with the admin UI off. */
const SHOW_ADMIN_UI_KEY = 'wms_show_admin_ui';
const AUTH_RECHECK_MS = 30_000;

function isAuthApiUrl(url: string | undefined): boolean {
  if (!url) return false;
  return url.includes('/api/auth/');
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isDev, setIsDev] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState<'user' | 'admin' | 'dev' | 'owner' | null>(null);
  const [showAdminUi, setShowAdminUi] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SHOW_ADMIN_UI_KEY) === 'true';
  });

  const authenticatedRef = useRef(false);
  const lastAuthRecheckAtRef = useRef(0);

  useEffect(() => {
    authenticatedRef.current = authenticated;
  }, [authenticated]);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SHOW_ADMIN_UI_KEY, String(showAdminUi));
  }, [showAdminUi]);

  useEffect(() => {
    if (!isAdmin && showAdminUi) {
      setShowAdminUi(false);
    }
  }, [isAdmin, showAdminUi]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error: unknown) => {
        if (axios.isAxiosError(error)) {
          const status = error.response?.status;
          const requestUrl = error.config?.url ?? '';
          if (
            status === 401
            && !isAuthApiUrl(requestUrl)
            && authenticatedRef.current
          ) {
            setSessionExpired(true);
          }
        }
        return Promise.reject(error);
      },
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  useEffect(() => {
    const recheckAuthOnVisible = (): void => {
      if (document.hidden || !authenticatedRef.current) {
        return;
      }
      const now = Date.now();
      if (now - lastAuthRecheckAtRef.current < AUTH_RECHECK_MS) {
        return;
      }
      lastAuthRecheckAtRef.current = now;

      void (async (): Promise<void> => {
        try {
          const response = await axios.get<AuthStatusResponse>('/api/auth/status');
          if (!response.data.authenticated) {
            setSessionExpired(true);
            setUser(null);
            setAuthenticated(false);
            setIsDev(false);
            setIsAdmin(false);
            setRole(null);
          }
        } catch (error: unknown) {
          console.error('Auth recheck on tab visible failed:', error);
        }
      })();
    };

    document.addEventListener('visibilitychange', recheckAuthOnVisible);
    return () => document.removeEventListener('visibilitychange', recheckAuthOnVisible);
  }, []);

  const toggleShowAdminUi = (): void => {
    setShowAdminUi((prev) => !prev);
  };

  const applyAuthenticatedStatus = (data: AuthStatusResponse): void => {
    setSessionExpired(false);
    setUser(data.user ?? null);
    setAuthenticated(true);
    setRole(data.role ?? null);
    setIsDev(data.role ? data.role === 'dev' : !!data.isDev);
    setIsAdmin(
      data.role
        ? data.role === 'admin' || data.role === 'dev'
        : !!data.isAdmin,
    );
  };

  const clearAuthenticatedStatus = (): void => {
    setUser(null);
    setAuthenticated(false);
    setIsDev(false);
    setIsAdmin(false);
    setRole(null);
  };

  const checkAuthStatus = async (): Promise<void> => {
    console.log('Checking auth status...');
    let reloading = false;
    try {
      const response = await axios.get<AuthStatusResponse>('/api/auth/status');
      console.log('Auth status response:', response.data);

      configureReceivingFromServer({
        receivingDataSource: response.data.receivingDataSource ?? 'supabase',
      });

      if (response.data.authenticated && startDailyHardRefresh()) {
        reloading = true;
        return;
      }

      if (response.data.authenticated) {
        console.log('Setting user as authenticated:', response.data.user);
        applyAuthenticatedStatus(response.data);
      } else {
        console.log('User not authenticated, clearing state');
        clearAuthenticatedStatus();
      }
    } catch (error: unknown) {
      console.error('Auth check failed:', error);
      configureReceivingFromServer({ receivingDataSource: 'supabase' });
      clearAuthenticatedStatus();
    } finally {
      if (!reloading) {
        console.log('Setting loading to false');
        setLoading(false);
      }
    }
  };

  const getBaseURL = (): string => {
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:5000';
    }
    return window.location.origin;
  };

  const login = (): void => {
    setUser(null);
    setAuthenticated(false);
    setSessionExpired(false);
    clearLocalStorageKeepingRefreshDay();
    sessionStorage.clear();

    const baseURL = getBaseURL();
    const returnTo = encodeURIComponent(window.location.href);
    window.location.href = `${baseURL}/api/auth/google?return_to=${returnTo}`;
  };

  const logout = async (): Promise<void> => {
    try {
      await axios.post('/api/auth/logout');
      setUser(null);
      setAuthenticated(false);
      setSessionExpired(false);
      sessionStorage.clear();
      window.location.href = '/';
    } catch (error: unknown) {
      console.error('Logout failed:', error);
      setUser(null);
      setAuthenticated(false);
      setSessionExpired(false);
      window.location.href = '/';
    }
  };

  const value: AuthContextValue = {
    user,
    authenticated,
    loading,
    sessionExpired,
    isDev,
    isAdmin,
    showAdminUi: isAdmin && showAdminUi,
    role,
    login,
    logout,
    toggleShowAdminUi,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
